import { useMutation, useQuery } from "@apollo/client";
import { Form, GridContainer, InnerForm } from "@fluidattacks/design";
import _ from "lodash";
import { Fragment, useCallback, useContext, useState } from "react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import type { Schema } from "yup";
import { object, string } from "yup";

import { ActionButtons } from "./action-buttons";
import { GeneralInformationSection } from "./details-tab/general-information-section";
import { PackageDetailsSection } from "./details-tab/package-details-section";
import { ReAttacksDetailsSection } from "./details-tab/reattacks-details-section";
import { TreatmentDetailsSection } from "./details-tab/treatment-details-section";
import {
  GET_VULN_ADDITIONAL_INFO,
  UPDATE_VULNERABILITY_DESCRIPTION,
} from "./queries";
import type { IAdditionalInfoProps, IFormValues } from "./types";

import type { IErrorInfoAttr } from "../upload-file/types";
import { authContext } from "context/auth";
import { Can } from "context/authz/can";
import type { VulnerabilitySource } from "gql/graphql";
import { useAudit } from "hooks/use-audit";
import { Logger } from "utils/logger";
import { msgError, msgSuccess } from "utils/notifications";

const AdditionalInfo: React.FC<IAdditionalInfoProps> = ({
  canRetrieveHacker,
  canSeeSource,
  refetchData,
  vulnerability,
}: IAdditionalInfoProps): JSX.Element => {
  const { t } = useTranslation();
  const { userEmail } = useContext(authContext);
  const [isEditing, setIsEditing] = useState(false);
  const regexExp = /^[A-Fa-f0-9]{40}$|^[A-Fa-f0-9]{64}$/u;

  const formatError = useCallback(
    (errorName: string, errorValue: string): string =>
      ` ${t(errorName)} "${errorValue}" ${t("groupAlerts.invalid")}. `,
    [t],
  );

  // Graphql operations
  const { addAuditEvent } = useAudit();
  const { data } = useQuery(GET_VULN_ADDITIONAL_INFO, {
    onCompleted: (): void => {
      addAuditEvent("Vulnerability", vulnerability.id);
    },
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        msgError(t("groupAlerts.errorTextsad"));
        Logger.warning(
          "An error occurred loading the vulnerability info",
          error,
        );
      });
    },
    variables: {
      canRetrieveHacker,
      vulnId: vulnerability.id,
    },
  });

  const [updateVulnerabilityDescription] = useMutation(
    UPDATE_VULNERABILITY_DESCRIPTION,
    {
      onCompleted: (mtResult): void => {
        if (mtResult.updateVulnerabilityDescription.success) {
          msgSuccess(
            t("searchFindings.tabVuln.additionalInfo.alerts.updatedDetails"),
            t("groupAlerts.updatedTitle"),
          );
          setIsEditing(false);
          refetchData();
        }
      },
      onError: (error): void => {
        error.graphQLErrors.forEach(({ message }): void => {
          if (message.includes("Exception - Error in path value")) {
            const errorObject: IErrorInfoAttr = JSON.parse(message);
            msgError(`${t("groupAlerts.portValue")}
            ${formatError("groupAlerts.value", errorObject.values)}`);
          } else if (message === "Invalid, vulnerability already exists") {
            msgError(t("validations.vulnerabilityAlreadyExists"));
          } else if (message === "Exception - Unsanitized input found") {
            msgError(t("validations.unsanitizedInputFound"));
          } else if (
            message.includes(
              "Exception - The vulnerability path does not exist in the toe lines",
            )
          ) {
            msgError(
              t(
                "searchFindings.tabVuln.alerts.uploadFile.linesPathDoesNotExist",
                {
                  path: t("searchFindings.tabVuln.vulnTable.location"),
                },
              ),
            );
          } else if (
            message.includes(
              "Exception -  The vulnerability URL and field do not exist in the toe inputs",
            )
          ) {
            msgError(
              t(
                "searchFindings.tabVuln.alerts.uploadFile.inputUrlAndFieldDoNotExist",
                {
                  path: t("searchFindings.tabVuln.vulnTable.location"),
                },
              ),
            );
          } else if (
            message.includes(
              "Exception -  The line does not exist in the range of 0 and lines of code",
            )
          ) {
            const destructMsg: { msg: string; path: string } =
              JSON.parse(message);
            msgError(
              t(
                "searchFindings.tabVuln.alerts.uploadFile.lineDoesNotExistInLoc",
                {
                  line: destructMsg.msg.split("code: ")[1],
                  path: t("searchFindings.tabVuln.vulnTable.location"),
                },
              ),
            );
          } else {
            msgError(t("groupAlerts.errorTextsad"));
            Logger.warning(
              "An error occurred updating the vulnerability description",
              error,
            );
          }
        });
      },
      refetchQueries: [
        {
          query: GET_VULN_ADDITIONAL_INFO,
          variables: {
            canRetrieveHacker,
            vulnId: vulnerability.id,
          },
        },
      ],
    },
  );

  // Handle action
  const toggleEdit = useCallback((): void => {
    setIsEditing((currentValue: boolean): boolean => !currentValue);
  }, []);

  const onSubmit = useCallback(
    async (values: IFormValues): Promise<void> => {
      await updateVulnerabilityDescription({
        variables: {
          commit: values.type === "lines" ? values.commitHash : undefined,
          source:
            values.source === "ASM"
              ? undefined
              : (values.source as VulnerabilitySource),
          vulnerabilityId: vulnerability.id,
        },
      });
    },
    [vulnerability, updateVulnerabilityDescription],
  );

  return (
    <React.StrictMode>
      <Form
        defaultValues={{
          commitHash: data?.vulnerability.commitHash ?? "",
          source: data?.vulnerability.source.toUpperCase() ?? "",
          type: data?.vulnerability.vulnerabilityType ?? "",
        }}
        id={"editVulnerability"}
        onSubmit={onSubmit}
        showButtons={false}
        yupSchema={object().shape({
          commitHash: string().when("type", ([type]: string[]): Schema => {
            return type === "lines"
              ? string()
                  .required(t("validations.required"))
                  .matches(regexExp, t("validations.commitHash"))
                  .nullable()
              : string().nullable();
          }),
          source: string().required(t("validations.required")),
        })}
      >
        <InnerForm<IFormValues>>
          {(methods): JSX.Element => {
            const commitHash = methods.watch("commitHash");
            const source = methods.watch("source");

            return (
              <Fragment>
                <Can
                  do={
                    "integrates_api_mutations_update_vulnerability_description_mutate"
                  }
                >
                  <ActionButtons
                    isEditing={isEditing}
                    isPristine={!methods.formState.isDirty}
                    onEdit={toggleEdit}
                    onUpdate={methods.handleSubmit(onSubmit)}
                  />
                </Can>
                <GridContainer lg={2} md={2} sm={1} xl={2}>
                  <GeneralInformationSection
                    canRetrieveHacker={canRetrieveHacker}
                    canSeeSource={canSeeSource}
                    commitHash={commitHash}
                    isEditing={isEditing}
                    userEmail={userEmail}
                    vulnerability={vulnerability}
                    vulnerabilityAdditionalInfo={data?.vulnerability}
                    vulnerabilitySource={source}
                    {...methods}
                  />
                  {!_.isNil(data?.vulnerability.advisories) &&
                    data.vulnerability.root?.__typename === "GitRoot" && (
                      <PackageDetailsSection
                        advisories={data.vulnerability.advisories}
                        groupName={data.vulnerability.groupName}
                        rootId={data.vulnerability.root.id}
                      />
                    )}
                  <TreatmentDetailsSection
                    vulnerability={vulnerability}
                    vulnerabilityAdditionalInfo={data?.vulnerability}
                  />
                  <ReAttacksDetailsSection
                    vulnerability={vulnerability}
                    vulnerabilityAdditionalInfo={data?.vulnerability}
                  />
                </GridContainer>
              </Fragment>
            );
          }}
        </InnerForm>
      </Form>
    </React.StrictMode>
  );
};

export { AdditionalInfo };
